import { ago, NoteId } from "@sp-crm/core";
import { AuthorName } from "components/byline/author-name";
import { IEntityNote } from "components/notes/edit";
import { Checkbox } from "components/ui/checkbox";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useState } from "react";

interface ImportantNoteAttributionProps {
    note: IEntityNote;
}

const ImportantNoteAttribution: React.FC<ImportantNoteAttributionProps> = props => {
    const { note } = props;
    return (
        <div className="text-xs text-brand-700 space-y-0.5">
            <p>
                Added by <AuthorName authorId={note.authorId} /> {ago(note.dateAdded)}
            </p>
            {note.wasEdited ? (
                <p>
                    Last edited by <AuthorName authorId={note.editorId} />{" "}
                    {ago(note.updatedAt)}
                </p>
            ) : null}
        </div>
    );
};

interface ImportantNoteEditorProps {
    note: IEntityNote;
    onEditNote: (noteId: NoteId, text: string, isPinned: boolean) => Promise<void>;
    leaveEditMode: () => void;
}

export const ImportantNoteEditor: React.FC<ImportantNoteEditorProps> = props => {
    const { note, onEditNote, leaveEditMode } = props;
    const [text, setText] = useState(note.text);
    const [isPinned, setIsPinned] = useState(note.isPinned);
    const [isSaving, setIsSaving] = useState(false);
    const submit = useCallback(async () => {
        setIsSaving(true);
        try {
            await onEditNote(note._id, text, isPinned);
        } finally {
            setIsSaving(false);
        }
    }, [text, isPinned, onEditNote, note._id]);
    return (
        <div className="space-y-2 mt-2">
            <TextArea
                rows={4}
                autoGrow
                value={text}
                onChange={v => setText(v.target.value)}
            />
            <div>
                <Checkbox
                    label="Pin as important"
                    labelClassName="text-sm text-gray-700"
                    checked={isPinned}
                    onChange={e => setIsPinned(e.target.checked)}
                />
            </div>
            <ImportantNoteAttribution note={note} />
            <div className="flex justify-end space-x-2">
                <SecondaryButton disabled={isSaving} onClick={leaveEditMode}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={isSaving} onClick={submit}>
                    {isSaving ? <Spinner /> : `Save`}
                </PrimaryButton>
            </div>
        </div>
    );
};
